import styled from '@emotion/styled';
import { useResource } from '@landsbankinn/l16n';
import { device, typography } from '@landsbankinn/ui';
import groupBy from 'lodash.groupby';
import React, { useMemo } from 'react';
import {
  Region,
  ServicePointType,
} from '../../../../__generated__/globalTypes';
import type { FetchServicePoints_servicePoints_nodes } from '../../../graphql/__generated__/FetchServicePoints';
import { Seperator } from '../../calculators/shared/Seperator';
import { Card } from '../../service-points/card/Card';
import { CardWrapper } from '../card/CardWrapper';
import { useRegionOptions } from '../filters/RegionDropdown';
import { useServiceLocationsContext } from '../ServicePoints';
import res from './servicePointCard.res';

const StyledCard = styled(Card)`
  padding-bottom: 24px;

  @media ${device.tablet} {
    min-height: 284px;
    padding-bottom: 0px;
  }
`;

const Name = styled.h3`
  ${typography.h3};
  margin-bottom: 6px;
`;

const Text = styled.p`
  ${typography.body};
`;

interface IProps {
  showOnMap?: boolean;
  location: FetchServicePoints_servicePoints_nodes;
}

interface ILocationsProps {
  locations: FetchServicePoints_servicePoints_nodes[];
}

export const Locations: React.FC<ILocationsProps> = ({ locations }) => {
  const { options } = useRegionOptions();
  const regionLocations = useMemo(
    () => groupBy(locations, (location) => location.region),
    [locations],
  );

  return (
    <>
      {Object.keys(regionLocations).map(function (key) {
        return (
          <CardWrapper
            key={key}
            title={options.find((op) => op.value === Region[key])?.label}
          >
            {regionLocations[key].map((l) => (
              <LocationCard key={l.id} location={l} showOnMap />
            ))}
          </CardWrapper>
        );
      })}
    </>
  );
};

export const LocationCard: React.FC<IProps> = ({
  location,
  children,
  showOnMap,
}) => {
  const { setIsMapOpen, setSelectedServicePoint, selectedType } =
    useServiceLocationsContext();
  const { r } = useResource();
  return (
    <>
      <StyledCard
        onClick={
          showOnMap
            ? () => {
                setSelectedServicePoint?.(location);
                setIsMapOpen?.(true);
              }
            : undefined
        }
      >
        <Name>
          {selectedType === ServicePointType.Branch &&
          location.branches &&
          location.branches.length > 0
            ? location.branches[0].name
            : location.name}
        </Name>
        <Text>{location.street}</Text>
        <Text>
          {location.postalCode} {location.city}
        </Text>

        {!showOnMap && location.branches && location.branches.length > 0 && (
          <Text>
            {r(res.bankNumber)} {location.branches[0].branchNumber}
          </Text>
        )}
        <Seperator border currentTheme="primary" />
        {children}
      </StyledCard>
    </>
  );
};
